import * as React from 'react';
import {Link, Redirect, matchPath, useHistory, useLocation} from 'react-router-dom';

/**
 * v5-backed compatibility shim for the handful of react-router v6 APIs the
 * ticketing client relied on. Lets call sites keep their v6-shaped bodies and
 * change only their import specifier. See ACC-471.
 *
 * NOT re-exported here (import these straight from react-router-dom, v5):
 *   useParams, useLocation, Link, Redirect, Switch, Route, MemoryRouter, BrowserRouter.
 */

type SetSearchParams = (
	next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
	opts?: {replace?: boolean}
) => void;

/** v6-shaped useSearchParams on top of v5 history + location. */
export function useSearchParams(): [URLSearchParams, SetSearchParams] {
	const location = useLocation();
	const history = useHistory();

	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

	const setSearchParams = React.useCallback<SetSearchParams>(
		(next, opts) => {
			const resolved = typeof next === 'function' ? next(new URLSearchParams(location.search)) : next;
			const search = resolved.toString();
			// Always carry the current pathname so a query-only update never drops it.
			const target = {pathname: location.pathname, search: search ? `?${search}` : ''};
			if (opts?.replace) history.replace(target);
			else history.push(target);
		},
		[history, location.pathname, location.search]
	);

	return [searchParams, setSearchParams];
}

type NavigateTo = string | number;

/** v6-shaped useNavigate. String paths push/replace; numbers delegate to history.go. */
export function useNavigate(): (to: NavigateTo, opts?: {replace?: boolean; state?: unknown}) => void {
	const history = useHistory();
	return React.useCallback(
		(to: NavigateTo, opts?: {replace?: boolean; state?: unknown}) => {
			if (typeof to === 'number') {
				history.go(to);
				return;
			}
			if (opts?.replace) history.replace(to, opts?.state);
			else history.push(to, opts?.state);
		},
		[history]
	);
}

type NavLinkClassName = string | ((args: {isActive: boolean}) => string);

export interface NavLinkProps extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> {
	to: string;
	/** When true, only an exact path match counts as active (v6 `end`). */
	end?: boolean;
	className?: NavLinkClassName;
}

/**
 * v6-shaped NavLink: supports the `className={({isActive}) => ...}` callback and
 * the `end` prop. Absolute `to` only — relative-`to` call sites were converted to
 * absolute paths in ACC-471 (settings tabs).
 */
export function NavLink({to, end = false, className, children, ...rest}: NavLinkProps) {
	const location = useLocation();
	const isActive = matchPath(location.pathname, {path: to, exact: end}) != null;
	const resolvedClassName = typeof className === 'function' ? className({isActive}) : className;
	return (
		<Link to={to} className={resolvedClassName} {...rest}>
			{children}
		</Link>
	);
}

export interface NavigateProps {
	to: string;
	/** v6 default is push; our call sites always pass replace. */
	replace?: boolean;
}

/** v6-shaped <Navigate> implemented as a v5 <Redirect>. */
export function Navigate({to, replace = false}: NavigateProps) {
	return <Redirect to={to} push={!replace} />;
}
